package com.karyadina.ui.screens.flashcards

import com.karyadina.model.ExamType
import com.karyadina.model.FlashCard_Subject
import com.karyadina.model.FlashcardStats
import com.karyadina.model.FlashcardPresets
import com.karyadina.model.FlashcardPreset
import com.karyadina.model.Unit as StudyUnit
import com.karyadina.service.FlashcardService
import com.karyadina.service.FlashcardServiceImpl
import com.karyadina.service.FlashcardStorageImpl
import com.karyadina.util.formatDateShort
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

// UI State holder
data class FlashcardUiState(
    val subjects: List<FlashCard_Subject> = emptyList(),
    val stats: FlashcardStats = FlashcardStats(),
    val selectedSubject: FlashCard_Subject? = null,
    val isLoading: Boolean = false,
    val error: String? = null
)

class FlashcardViewModel(
    private val service: FlashcardService = FlashcardServiceImpl(FlashcardStorageImpl()) // Default injection
) {
    // ViewModel scope (Generic CoroutineScope for MPP)
    private val viewModelScope = CoroutineScope(Dispatchers.Main)
    
    private val _uiState = MutableStateFlow(FlashcardUiState())
    val uiState: StateFlow<FlashcardUiState> = _uiState.asStateFlow()
    
    init {
        loadData()
        
        // Observe storage refresh signals (e.g. from cloud sync)
        viewModelScope.launch {
            com.karyadina.service.AppStorage.refreshNotifier.collect {
                service.refreshFromStorage()
                refresh()
            }
        }
    }
    
    fun loadData() {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }
            try {
                // Determine if we need to filter by current selected subject to refresh its view?
                // For now, reload main list and stats
                refresh()
            } catch (e: Exception) {
                _uiState.update { it.copy(error = e.message, isLoading = false) }
            }
        }
    }
    
    fun refresh() {
        val subjects = service.getSubjects()
        val stats = service.getStats()
        _uiState.update { 
            it.copy(
                subjects = subjects, 
                stats = stats, 
                isLoading = false
            ) 
        }
    }
    
    fun createSubject(name: String, examType: ExamType, color: String) {
        service.createSubject(name, examType, color)
        refresh()
    }
    
    fun deleteSubject(subjectId: String) {
        service.deleteSubject(subjectId)
        // If the deleted subject was selected, clear selection
        if (_uiState.value.selectedSubject?.id == subjectId) {
            clearSelectedSubject()
        }
        refresh()
    }
    
    fun selectSubject(subject: FlashCard_Subject) {
        _uiState.update { it.copy(selectedSubject = subject) }
    }
    
    fun clearSelectedSubject() {
        _uiState.update { it.copy(selectedSubject = null) }
        refresh() // Refresh to ensure stats are up to date when returning
    }
    
    fun getSubjectStats(subjectId: String) = service.getSubjectStats(subjectId)
    
    /**
     * Calculate 7-day mastery trend from review history
     * Returns: Pair of (data points, date labels)
     * 
     * How it works:
     * - Gets all review sessions from the last 7 days
     * - Groups them by calendar day (actual real dates)
     * - Calculates: correctAnswers / cardsReviewed * 100 for each day
     * - Returns trend list (0-100% per day) with actual dates as labels
     * 
     * Example: [60f, 62f, 65f, 68f, 70f, 72f, 74f] with ["Mar 25", "Mar 26", ...]
     */
    fun get7DayMasteryTrend(): Pair<List<Float>, List<String>> {
        val reviewHistory = service.getReviewHistory()
        val now = com.karyadina.util.currentTimeMillis()
        val sevenDaysAgo = now - (7 * 24 * 60 * 60 * 1000) // 7 days in milliseconds
        
        // Filter sessions from last 7 days and that are completed
        val recentSessions = reviewHistory.filter { session ->
            session.completed && session.startTime >= sevenDaysAgo
        }
        
        // Create list of the last 7 days (from 6 days ago to today)
        val last7DaysMs = mutableListOf<Long>()
        for (i in 6 downTo 0) {
            last7DaysMs.add(now - (i * 24 * 60 * 60 * 1000))
        }
        
        // Create map of day timestamp to list of sessions
        val sessionsByDay = mutableMapOf<Long, MutableList<com.karyadina.model.ReviewSession>>()
        last7DaysMs.forEach { dayMs ->
            val dayStart = (dayMs / (24 * 60 * 60 * 1000)) * (24 * 60 * 60 * 1000)
            sessionsByDay[dayStart] = mutableListOf()
        }
        
        recentSessions.forEach { session ->
            val dayStart = (session.startTime / (24 * 60 * 60 * 1000)) * (24 * 60 * 60 * 1000)
            if (dayStart in sessionsByDay) {
                sessionsByDay[dayStart]?.add(session)
            }
        }
        
        // Calculate mastery % for each day and create labels
        val trendData = mutableListOf<Float>()
        val labels = mutableListOf<String>()
        
        last7DaysMs.forEach { dayMs ->
            val dayStart = (dayMs / (24 * 60 * 60 * 1000)) * (24 * 60 * 60 * 1000)
            val daysSessions = sessionsByDay[dayStart] ?: emptyList()
            
            // Get mastery % for this day
            val mastery = if (daysSessions.isEmpty()) {
                0f
            } else {
                val totalCorrect = daysSessions.sumOf { it.correctAnswers }
                val totalCards = daysSessions.sumOf { it.cardsReviewed }
                if (totalCards > 0) {
                    (totalCorrect.toFloat() / totalCards) * 100f
                } else {
                    0f
                }
            }
            trendData.add(mastery)
            
            // Create date label (e.g., "Apr 1", "Today", "Yesterday", etc)
            val label = formatDateShort(dayStart)
            labels.add(label)
        }
        
        return Pair(trendData, labels)
    }
    
    // Pass-through service accessor if needed by child components for granular operations
    // Ideally we'd wrap these too, but effectively 'service' is part of the VM API for now
    fun getService(): FlashcardService = service
    
    fun createUnit(subjectId: String, name: String, order: Int) {
        service.createUnit(subjectId, name, order)
        refresh()
    }

    fun createBatchFlashcards(preset: FlashcardPreset) {
        viewModelScope.launch {
            _uiState.update { it.copy(isLoading = true) }
            try {
                preset.subjects.forEach { presetSubject ->
                    // 1. Create Subject
                    val subject = service.createSubject(
                        name = presetSubject.name,
                        examType = preset.examType,
                        color = presetSubject.color
                    )
                    
                    // 2. Create Units
                    presetSubject.units.forEachIndexed { index, unitName ->
                        service.createUnit(
                            subjectId = subject.id,
                            name = unitName,
                            order = index
                        )
                    }
                }
                refresh()
            } catch (e: Exception) {
                 _uiState.update { it.copy(error = e.message, isLoading = false) }
            }
        }
    }
}
